import { MessageCircle, Phone, Mail, MapPin, Clock, User, ArrowUpRight } from "lucide-react";
import { CONTACT } from "@/lib/data";
import { useI18n } from "@/context/LanguageContext";
import { scrollToId } from "@/hooks/useLenis";

export const Footer = () => {
  const { t } = useI18n();
  const f = t.footer;
  const QUICK = [
    { label: f.links.services, href: "#services" },
    { label: f.links.capabilities, href: "#capabilities" },
    { label: f.links.portfolio, href: "#portfolio" },
    { label: f.links.quality, href: "#quality" },
    { label: f.links.quote, href: "#quote" },
  ];

  return (
    <footer className="bg-ink text-white relative noise">
      <div className="h-2 stripes" />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <div className="stripes w-10 h-10 flex items-center justify-center border-2 border-white/20">
              <span className="font-display font-700 text-lg text-white mix-blend-difference">N</span>
            </div>
            <div>
              <div className="font-display font-700 text-xl">NIDHI<span className="text-safety"> FABRICATION</span></div>
              <div className="font-mono2 text-[10px] tracking-[0.3em] text-white/40">{t.brandSub}</div>
            </div>
          </div>
          <p className="text-white/50 text-sm max-w-sm leading-relaxed mb-6">{f.desc}</p>
          <a
            href={`https://wa.me/${CONTACT.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-whatsapp"
            className="inline-flex items-center gap-2 px-5 h-12 bg-safety text-white font-mono2 text-xs font-700 uppercase tracking-wider hover:translate-x-[2px] hover:translate-y-[2px] transition-transform hard-shadow-sm"
          >
            <MessageCircle size={16} /> {f.quickWhatsapp}
          </a>
        </div>

        <div className="lg:col-span-3">
          <h4 className="font-mono2 text-[11px] uppercase tracking-widest text-safety mb-6">{f.navigate}</h4>
          <ul className="space-y-3">
            {QUICK.map((q, i) => (
              <li key={i}>
                <a href={q.href} className="group flex items-center gap-1.5 text-white/70 hover:text-white text-sm transition-colors">
                  <ArrowUpRight size={14} className="text-safety opacity-0 group-hover:opacity-100 transition-opacity" />
                  {q.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-4">
          <h4 className="font-mono2 text-[11px] uppercase tracking-widest text-safety mb-6">{f.workshop}</h4>
          <ul className="space-y-4 text-sm">
            <li className="flex items-center gap-3 text-white/70">
              <User size={16} className="text-safety flex-shrink-0" /> {CONTACT.contactPerson}
            </li>
            <li className="flex items-start gap-3 text-white/70">
              <MapPin size={16} className="text-safety flex-shrink-0 mt-0.5" /> {CONTACT.address}
            </li>
            <li className="flex items-center gap-3 text-white/70">
              <Phone size={16} className="text-safety flex-shrink-0" />
              <span>
                <a href={`tel:${CONTACT.phoneRaw}`} className="hover:text-white">{CONTACT.phone}</a>
                {" · "}
                <a href={`tel:${CONTACT.phone2Raw}`} className="hover:text-white">{CONTACT.phone2}</a>
              </span>
            </li>
            <li className="flex items-center gap-3 text-white/70">
              <Mail size={16} className="text-safety flex-shrink-0" />
              <a href={`mailto:${CONTACT.email}`} className="hover:text-white break-all">{CONTACT.email}</a>
            </li>
            <li className="flex items-center gap-3 text-white/70">
              <Clock size={16} className="text-safety flex-shrink-0" /> {CONTACT.hours}
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 font-mono2 text-[11px] text-white/40 tracking-wide">
          <span>© {new Date().getFullYear()} Nidhi Fabrication. {f.rights}</span>
          <span className="flex items-center gap-4">
            <span>GSTIN: {CONTACT.gstin} · ISO 9001:2015 · MSME</span>
            <a href="/admin" data-testid="footer-admin-link" className="text-white/30 hover:text-safety transition-colors">Admin</a>
          </span>
        </div>
      </div>
    </footer>
  );
};
